import type { OperationKind } from '@flux/shared';
import { Archive } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { JSX, MouseEvent, ReactNode } from 'react';
import { Button, type ButtonProps } from '@/components/ui/button.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
import { ApiError } from '@/lib/api/client.js';
import { cn } from '@/lib/cn.js';
import { formatCountLabel } from '../subscriptions/money.js';

/** Pièces communes aux fiches d'opération (produit offert et remise renouvellement). */

/** Message d'une erreur de mutation : celui de l'API, déjà en français courant (`04` §6). */
export function mutationErrorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Une erreur technique est survenue. Réessayez.';
}

/**
 * Corps du dialogue d'archivage : conséquence, irréversibilité (pas de
 * désarchivage) et, pour une remise désactivée dont des remises sont encore
 * suivies, la réconciliation qui continue (`07` §4.3).
 */
export function archiveDescription(kind: OperationKind, trackedDiscounts: number): ReactNode {
  return (
    <>
      <p>
        {kind === 'free_product'
          ? 'L’opération ne s’appliquera plus à aucune commande et disparaît de la liste ; elle reste consultable en cochant « Afficher les archivées ».'
          : 'L’opération ne pourra plus être activée et disparaît de la liste ; elle reste consultable en cochant « Afficher les archivées ».'}
      </p>
      {trackedDiscounts > 0 ? (
        <p className="mt-2">
          {`${formatCountLabel(trackedDiscounts, 'remise reste', 'remises restent')} en place : la réconciliation quotidienne continue de les suivre jusqu’au renouvellement.`}
        </p>
      ) : null}
      <p className="mt-2 font-medium text-foreground">Impossible de la restaurer.</p>
    </>
  );
}

/**
 * Échec d'enregistrement en tête de formulaire, loin du bouton : à chaque
 * nouveau message, la bannière défile dans la vue et prend le focus.
 */
export function SubmitErrorBanner({ message }: { message: string | null }): JSX.Element | null {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (message === null || element === null) {
      return;
    }
    // jsdom n'implémente pas `scrollIntoView`.
    if ('scrollIntoView' in element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'center' });
    }
    element.focus({ preventScroll: true });
  }, [message]);
  if (message === null) {
    return null;
  }
  return (
    <p
      ref={ref}
      role="alert"
      tabIndex={-1}
      className="rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {`Enregistrement impossible : ${message}`}
    </p>
  );
}

/** Bannière de page d'une opération archivée. */
export function ArchivedBanner({ kind }: { kind: OperationKind }): JSX.Element {
  return (
    <p
      data-testid="operation-archived-banner"
      className="flex items-start gap-2 rounded-md border border-status-gray-border bg-status-gray-bg px-3 py-2 text-sm text-status-gray-fg"
    >
      <Archive className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>
        <span className="font-medium">Opération archivée</span>
        {kind === 'free_product'
          ? ' · Elle ne s’applique plus à aucune commande.'
          : ' · Elle ne peut plus être activée ; les remises encore en place restent suivies par la réconciliation.'}
      </span>
    </p>
  );
}

export interface GatedButtonProps extends Omit<ButtonProps, 'disabled' | 'onClick'> {
  /** Raison de l'indisponibilité ; `null` = bouton actif. */
  reason: string | null;
  /** `id` du texte de la raison, relié par `aria-describedby`. */
  reasonId: string;
  onClick: () => void;
}

/**
 * Bouton indisponible qui reste visible et focusable (`aria-disabled`) : la
 * raison s'affiche en infobulle au survol et au focus, et reste lue par les
 * lecteurs d'écran.
 */
export function GatedButton({ reason, reasonId, onClick, className, children, ...props }: GatedButtonProps): JSX.Element {
  if (reason === null) {
    return (
      <Button type="button" className={className} onClick={onClick} {...props}>
        {children}
      </Button>
    );
  }
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            aria-disabled="true"
            aria-describedby={reasonId}
            className={cn('cursor-not-allowed opacity-50', className)}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
            }}
            {...props}
          >
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{reason}</TooltipContent>
      </Tooltip>
      <span id={reasonId} className="sr-only">
        {reason}
      </span>
    </>
  );
}
